import { z } from "zod";

type ImportMetaValues = Pick<ImportMetaEnv,
  | "VITE_SUPABASE_URL"
  | "VITE_SUPABASE_PUBLISHABLE_KEY"
  | "VITE_FEATURES_LEADERSHIP_DASHBOARD"
  | "VITE_FEATURES_PERFORMANCE_CENTER"
  | "VITE_FEATURES_HIERARCHICAL_DASHBOARD"
  | "VITE_FEATURES_MULTI_TENANCY"
  | "VITE_FEATURES_CALENDAR_RELIABILITY_V2"
  | "VITE_FEATURES_WILL_ONBOARDING"
  | "VITE_GHL_API_KEY"
  | "VITE_GHL_LOCATION_ID"
  | "VITE_GHL_WEBHOOK_URL"
  | "VITE_AZURE_CLIENT_ID"
>;

const rawEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url({ message: "VITE_SUPABASE_URL must be a valid URL" }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(1, "VITE_SUPABASE_PUBLISHABLE_KEY is required"),
  VITE_FEATURES_LEADERSHIP_DASHBOARD: z.coerce.boolean().optional().default(false),
  VITE_FEATURES_PERFORMANCE_CENTER: z.coerce.boolean().optional().default(false),
  VITE_FEATURES_HIERARCHICAL_DASHBOARD: z.coerce.boolean().optional().default(false),
  VITE_FEATURES_MULTI_TENANCY: z.coerce.boolean().optional().default(false),
  VITE_FEATURES_CALENDAR_RELIABILITY_V2: z.coerce.boolean().optional().default(true),
  VITE_FEATURES_WILL_ONBOARDING: z.coerce.boolean().optional().default(false),
  VITE_GHL_API_KEY: z.string().optional(),
  VITE_GHL_LOCATION_ID: z.string().optional(),
  VITE_GHL_WEBHOOK_URL: z.string().url().optional(),
  VITE_AZURE_CLIENT_ID: z.string().optional(),
});

const parsed = rawEnvSchema.parse({
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  VITE_FEATURES_LEADERSHIP_DASHBOARD: import.meta.env.VITE_FEATURES_LEADERSHIP_DASHBOARD,
  VITE_FEATURES_PERFORMANCE_CENTER: import.meta.env.VITE_FEATURES_PERFORMANCE_CENTER,
  VITE_FEATURES_HIERARCHICAL_DASHBOARD: import.meta.env.VITE_FEATURES_HIERARCHICAL_DASHBOARD,
  VITE_FEATURES_MULTI_TENANCY: import.meta.env.VITE_FEATURES_MULTI_TENANCY,
  VITE_FEATURES_CALENDAR_RELIABILITY_V2: import.meta.env.VITE_FEATURES_CALENDAR_RELIABILITY_V2,
  VITE_FEATURES_WILL_ONBOARDING: import.meta.env.VITE_FEATURES_WILL_ONBOARDING,
  VITE_GHL_API_KEY: import.meta.env.VITE_GHL_API_KEY,
  VITE_GHL_LOCATION_ID: import.meta.env.VITE_GHL_LOCATION_ID,
  VITE_GHL_WEBHOOK_URL: import.meta.env.VITE_GHL_WEBHOOK_URL,
  VITE_AZURE_CLIENT_ID: import.meta.env.VITE_AZURE_CLIENT_ID,
} satisfies ImportMetaValues);

export const env = {
  supabase: {
    url: parsed.VITE_SUPABASE_URL,
    anonKey: parsed.VITE_SUPABASE_PUBLISHABLE_KEY,
  },
  features: {
    leadershipDashboard: parsed.VITE_FEATURES_LEADERSHIP_DASHBOARD,
    performanceCenter: parsed.VITE_FEATURES_PERFORMANCE_CENTER,
    hierarchicalDashboard: parsed.VITE_FEATURES_HIERARCHICAL_DASHBOARD,
    multiTenancy: parsed.VITE_FEATURES_MULTI_TENANCY,
    calendarReliabilityV2: parsed.VITE_FEATURES_CALENDAR_RELIABILITY_V2,
    willOnboarding: parsed.VITE_FEATURES_WILL_ONBOARDING,
  },
  ghl: {
    apiKey: parsed.VITE_GHL_API_KEY ?? null,
    locationId: parsed.VITE_GHL_LOCATION_ID ?? null,
    webhookUrl: parsed.VITE_GHL_WEBHOOK_URL ?? null,
  },
  azure: {
    clientId: parsed.VITE_AZURE_CLIENT_ID ?? null,
  },
} as const;

export type AppEnv = typeof env;
